import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { previewPages } from '../data/previews';
import { productShortName } from '../data/offer';
export function LookInsideCarousel() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const total = previewPages.length;
  const goTo = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const next = (index + total) % total;
    const slide = track.children[next] as HTMLElement | undefined;
    if (!slide) return;
    track.scrollTo({
      left: slide.offsetLeft - (track.clientWidth - slide.clientWidth) / 2,
      behavior: 'smooth'
    });
  }, [total]);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const onScroll = () => {
      const center = track.scrollLeft + track.clientWidth / 2;
      let closest = 0;
      let distance = Infinity;
      Array.from(track.children).forEach((child, index) => {
        const el = child as HTMLElement;
        const d = Math.abs(el.offsetLeft + el.clientWidth / 2 - center);
        if (d < distance) {
          distance = d;
          closest = index;
        }
      });
      setActive(closest);
    };
    onScroll();
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => track.removeEventListener('scroll', onScroll);
  }, []);
  const navButton = 'absolute top-1/2 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-slate-600 bg-slate-900 text-white shadow-lg shadow-black/40 transition-colors duration-150 ease-out hover:bg-slate-800 focus:outline-none focus-visible:ring-4 focus-visible:ring-amber-400/50';
  return <section className="bg-slate-950 py-16 sm:py-20" aria-labelledby="inside-title" data-id="element-104" data-mp="3voqtsp1ifrxz">
      <div className="mx-auto max-w-6xl px-4" data-id="element-105" data-mp="3qpw7hd4c3l94">
        <div className="text-center" data-id="element-106" data-mp="3qr7b9cxs4t08">
          <p className="text-sm font-extrabold uppercase tracking-[.16em] text-amber-400" data-id="element-107" data-mp="3qo32e29dd938">Look Inside</p>
          <h2 id="inside-title" className="mx-auto mt-3 max-w-4xl text-balance text-3xl font-extrabold uppercase leading-tight tracking-tight text-white sm:text-4xl" data-id="element-108" data-mp="1trgig225qq4m">
            Discover What&apos;s Inside <span className="text-amber-400" data-id="element-109" data-mp="1f0pu53zry969">{productShortName}</span>
          </h2>
          <span className="mx-auto mt-4 flex w-fit items-center gap-1" aria-hidden="true" data-id="element-110" data-mp="1jzkkcr02n00e">
            <span className="h-1 w-10 rounded-full bg-slate-600" data-id="element-111" data-mp="27s102uwc6fwj" />
            <span className="h-1 w-3 rounded-full bg-amber-400" data-id="element-112" data-mp="2cp9o3c461e5p" />
          </span>
          <p className="mx-auto mt-6 max-w-3xl leading-relaxed text-slate-300" data-id="element-113" data-mp="1yr2x8fxmwefj">
            Fluid power principles, Pascal&apos;s law, pressure, flow and force calculations, ISO 1219 circuit symbols,
            gear, vane and piston pumps, hydraulic motors and cylinders, hydraulic power units, accumulators, directional,
            pressure and flow control valves, circuit design, hydraulic fluids, filtration and ISO 4406 cleanliness,
            compressed air production and treatment, pneumatic valves and actuators, preventive maintenance, systematic
            troubleshooting, lockout and stored-energy safety and much more.
          </p>
        </div>

        <div className="relative mt-10" role="region" aria-roledescription="carousel" aria-label="Sample pages" data-id="element-114" data-mp="tpaeqhhtxjvh">
          <div ref={trackRef} className="flex touch-pan-x snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth px-[10%] pb-2 [-webkit-overflow-scrolling:touch] [scrollbar-width:none] sm:px-[20%] md:px-[35%] [&::-webkit-scrollbar]:hidden" data-id="element-115" data-mp="12z56hc8w8mj">
            {previewPages.map((page, index) => <figure key={page.src} className={`w-[80%] shrink-0 snap-center overflow-hidden rounded-2xl border bg-slate-900 shadow-lg shadow-black/40 transition-[border-color,opacity] duration-200 ease-out sm:w-[60%] md:w-[30%] ${index === active ? 'border-amber-400/70 opacity-100' : 'border-slate-700 opacity-70'}`} aria-roledescription="slide" aria-label={`${index + 1} of ${total}`} data-id="element-116" data-mp="auv9wb8u6wzq" data-mpi={index}>
                <img src={page.src} alt={page.alt} loading="lazy" draggable={false} className="block aspect-[4/5] w-full select-none object-cover" data-id="element-117" data-mp="2cpay06ui9g2b" />
              </figure>)}
          </div>

          <button type="button" onClick={() => goTo(active - 1)} className={`${navButton} left-0 sm:-left-2`} aria-label="Previous page" data-id="element-118" data-mp="2lqissextbyrn">
            <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-119" data-mp="2vhvl17niwqy7" />
          </button>
          <button type="button" onClick={() => goTo(active + 1)} className={`${navButton} right-0 sm:-right-2`} aria-label="Next page" data-id="element-120" data-mp="2vk863tokvhfh">
            <ChevronRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-121" data-mp="34ibpokg0644n" />
          </button>
        </div>

        <div className="mt-6 flex justify-center gap-2" data-id="element-122" data-mp="39glkuv1q94q9">
          {previewPages.map((page, index) => <button key={page.src} type="button" onClick={() => goTo(index)} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 ${index === active ? 'w-6 bg-amber-400' : 'w-2 bg-slate-600 hover:bg-slate-500'}`} aria-label={`Go to page ${index + 1}`} aria-current={index === active} data-id="element-123" data-mp="1a5c4mbsushpz" data-mpi={index} />)}
        </div>
      </div>
    </section>;
}